/**
 * Saving, as a promise rather than a payment.
 *
 * Nothing in the ledger records a saving. A goal says what you mean to put by
 * and when it has to be there; from that, OKANE works out an instalment and
 * pretends you commit it on schedule. The money is not moved anywhere — it is
 * simply taken out of what you are allowed to think you have.
 *
 * Instalments are anchored on the day the saving ends and counted backwards, so
 * a goal that lands on the 31st commits on the 31st of every month — or the
 * nearest day a shorter month has.
 */
import { addDays, addMonths, monthOf, today as todayISO } from './dates'
import { round2 } from './money'

export const SAVING_CADENCES = [
  { value: 'weekly', perYear: 52 },
  { value: 'fortnightly', perYear: 26 },
  { value: 'monthly', perYear: 12 },
  { value: 'quarterly', perYear: 4 },
]

const DAY_STEP = { weekly: 7, fortnightly: 14 }
const MONTH_STEP = { monthly: 1, quarterly: 3 }

export const cadenceOf = (goal) =>
  SAVING_CADENCES.find((c) => c.value === goal?.cadence)?.value ?? 'monthly'

/** The key for the word; this file says nothing in any language. */
export const cadenceKey = (value) =>
  SAVING_CADENCES.some((c) => c.value === value) ? `cadence.${value}` : 'cadence.monthly'

/** `steps` instalments back from `anchor` — negative steps go forwards. */
export function shiftByCadence(anchor, cadence, steps) {
  const days = DAY_STEP[cadence]
  if (days) return addDays(anchor, days * steps)
  return addMonths(anchor, (MONTH_STEP[cadence] ?? 1) * steps)
}

const startOf = (goal) => goal.start_date || String(goal.created_at ?? todayISO()).slice(0, 10)

/**
 * Every day this goal commits an instalment, oldest first. They end on the
 * target date and step backwards from it, never earlier than the day the goal
 * was started.
 */
export function instalmentDates(goal, end) {
  const finish = end ?? goal.target_date
  if (!finish) return []
  const start = startOf(goal)
  const cadence = cadenceOf(goal)
  const dates = []
  for (let step = 0; step < 600; step++) {
    const date = shiftByCadence(finish, cadence, -step)
    if (date <= start) break
    dates.push(date)
  }
  return dates.reverse()
}

/**
 * What a goal is worth today, and what it takes to get there.
 *
 * @param {object} goal
 * @param {string} [today]
 * @returns {object} the instalment, the schedule, and what is already set aside
 */
export function goalPlan(goal, today = todayISO()) {
  const target = Math.abs(Number(goal.target_amount) || 0)
  const initial = Math.abs(Number(goal.initial_amount) || 0)
  const outstanding = Math.max(0, target - initial)
  const start = startOf(goal)
  const cadence = cadenceOf(goal)

  let dates = []
  let perInstalment = 0

  if (goal.pace_mode === 'by_amount' && Number(goal.monthly_amount) > 0) {
    // You named the instalment; the finishing date follows from it.
    perInstalment = Math.abs(Number(goal.monthly_amount))
    const count = outstanding > 0 ? Math.ceil(outstanding / perInstalment) : 0
    const end = count ? shiftByCadence(start, cadence, count) : start
    dates = instalmentDates({ ...goal, target_date: end }, end)
  } else if (goal.target_date) {
    // You named the day; the instalment follows from it.
    dates = instalmentDates(goal, goal.target_date)
    perInstalment = dates.length ? outstanding / dates.length : outstanding
  }

  // The last instalment settles the remainder, so rounding can never leave a
  // goal a few cents short of itself — nor a few over.
  const schedule = dates.map((date, i) => ({
    date,
    amount: round2(
      i === dates.length - 1 ? outstanding - perInstalment * (dates.length - 1) : perInstalment,
    ),
  }))
  const committed = schedule.filter((i) => i.date <= today)
  // What was already put by was put by on the day the saving started — before
  // that the goal held nothing, and the balance was yours to spend.
  const held = start <= today ? initial : 0
  const setAside = round2(
    Math.min(target, held + committed.reduce((sum, i) => sum + i.amount, 0)),
  )
  const next = dates.find((date) => date > today) ?? null

  return {
    goal,
    cadence,
    start,
    target,
    initial,
    perInstalment: round2(perInstalment),
    dates,
    schedule,
    committed: committed.length,
    instalments: dates.length,
    setAside,
    remaining: round2(Math.max(0, target - setAside)),
    progress: target > 0 ? Math.min(1, setAside / target) : 0,
    next,
    end: dates[dates.length - 1] ?? null,
    complete: target > 0 && setAside >= target,
  }
}

/**
 * A goal written out as if it were a ledger: what was already put by on the day
 * the saving started, then one entry per instalment it has taken since.
 *
 * It stops at today. An instalment still to come has not been taken out of the
 * balance either — `setAsideOn` counts the same rows — so writing one down
 * would show money leaving that is still there.
 *
 * These are not transactions and never become any — no money moved, the balance
 * already accounts for them, and nothing can be edited. They exist so that the
 * pretending is *visible*: you can see, in the list, the money leaving on the
 * day the goal takes it.
 */
export function savingsEntries(goal, today = todayISO()) {
  if (!goal || goal.archived) return []
  const plan = goalPlan(goal, today)
  const rows = []
  if (plan.initial > 0 && plan.start <= today) {
    rows.push({ date: plan.start, amount: plan.initial, opening: true })
  }
  for (const instalment of plan.schedule) {
    if (instalment.amount > 0 && instalment.date <= today) {
      rows.push({ date: instalment.date, amount: instalment.amount })
    }
  }
  return rows.map((row) => ({
    id: `sv_${goal.id}_${row.date}${row.opening ? '_opening' : ''}`,
    date: row.date,
    // Sorting ties break on this; a goal takes its cut once the day's real
    // money has moved, so it sits at the end of its day.
    created_at: `${row.date}T23:59:59.999Z`,
    description: goal.name,
    amount: -row.amount,
    // A goal is a category of its own — a *virtual* one: it lives in no table
    // and nothing real can be filed under it, but it can be filtered by and
    // drawn as a line like any other. The goal's name is its name.
    category: goal.name,
    virtualCategory: true,
    notes: '',
    payment_type: '',
    kind: 'savings',
    // The flag the ledger reads: shown, never selected, never edited.
    saving: true,
    goalId: goal.id,
    opening: !!row.opening,
  }))
}

/** What every goal has taken out of the balance by a given day. */
export function setAsideOn(goals, date = todayISO()) {
  return round2(
    (goals ?? [])
      .filter((goal) => !goal.archived)
      .reduce((sum, goal) => sum + goalPlan(goal, date).setAside, 0),
  )
}

/** The instalments falling between two days (`from` exclusive), across every goal. */
export function instalmentsBetween(goals, from, to) {
  let total = 0
  for (const goal of goals ?? []) {
    if (goal.archived) continue
    for (const i of goalPlan(goal, from).schedule) {
      if (i.date > from && i.date <= to) total += i.amount
    }
  }
  return round2(total)
}

/** What each goal still owes before this month is out. */
export const dueThisMonth = (goals, today = todayISO()) =>
  instalmentsBetween(goals, today, `${monthOf(today)}-31`)
